// src/components/WeeklyTrailerCalendar/WeeklyTrailerCalendar.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useBooking } from "../../context/BookingContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import {
  MdCalendarToday,
  MdArrowBack,
  MdArrowForward,
  MdKeyboardArrowUp,
  MdCheckCircle,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";

/**
 * Tarkistaa, meneekö aikavälit (start1–end1) ja (start2–end2) päällekkäin.
 */
const isOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && start2 < end1;
};

/**
 * Palauttaa saman viikon maanantain annetusta päivämäärästä.
 */
const getCurrentWeekMonday = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getMonthNumber = (date) => {
  const month = date.getMonth() + 1;
  return String(month).padStart(2, "0");
};

/**
 * Muodostaa tunnin merkkijonoksi hh:mm.
 */
const formatSlotTime = (hour) => `${String(hour).padStart(2, "0")}:00`;

/**
 * Lisää 'days' päivää annettuun päivämäärään.
 */
const addDays = (date, days) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

/**
 * Lasketaan ISO-standardin mukainen viikkonumero.
 */
const getISOWeekNumber = (date) => {
  const temp = new Date(date.getTime());
  temp.setHours(0, 0, 0, 0);
  temp.setDate(temp.getDate() + 3 - ((temp.getDay() + 6) % 7));
  const year = temp.getFullYear();
  const firstThursday = new Date(year, 0, 4);
  const dayOfYear = Math.round((temp - firstThursday) / 86400000) + 1;
  return 1 + Math.floor((dayOfYear - 1) / 7);
};

const calendarCardClass =
  "my-10 mx-auto flex max-w-screen-xl flex-col gap-6 overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-6 lg:p-8";

const badgeClass =
  "inline-flex rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase tracking-[0.14em] !text-[#111827] shadow-sm dark:!text-white";

const headingClass =
  "font-customFont2 text-4xl font-bold uppercase leading-none !text-[#111827] dark:!text-white sm:text-5xl lg:text-6xl";

const mutedTextClass =
  "font-customFont1 text-sm font-semibold leading-relaxed !text-[#4B5563] dark:!text-[#D1D5DB]";

const inputClass =
  "w-full cursor-default rounded-2xl border border-[#DDE7E5] bg-[#F8FAFC] px-4 py-3 text-center font-customFont1 text-lg font-extrabold text-[#111827] shadow-sm outline-none dark:border-darkborder dark:bg-darksurface dark:text-white";

const WeeklyTrailerCalendar = ({ reservations }) => {
  const { selectedDates, setSelectedDates, setTrailerPickupTime } = useBooking();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("trailerPickupTime")) {
      localStorage.setItem("trailerPickupTime", "07:00");
    }
  }, []);

  /**
   * Määritellään weekStart:
   *  - Jos selectedDates.startDate on asetettu → käytetään sitä
   *  - Muuten käytetään tämän päivän aikaa
   *  - Ja haetaan kuluvan viikon maanantai.
   */
  const [weekStart, setWeekStart] = useState(() => {
    const baseDate = selectedDates?.startDate
      ? new Date(selectedDates.startDate)
      : new Date();

    return getCurrentWeekMonday(baseDate);
  });

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Käytetään absoluuttisia varausaikoja
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  // Myös suhteelliset valinnat
  const [startPick, setStartPick] = useState(null);

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  // Näytetään vain ajat 06:00–00:00 (eli 6–23)
  const slotsArray = Array.from({ length: 18 }, (_, i) => i + 6);

  const hasTrailer = useMemo(() => reservations.length > 0, [reservations]);

  // Asetetaan selectedDates localStoragesta, jos niitä ei vielä ole
  useEffect(() => {
    if (!localStorage.getItem("modalBookingData")) {
      const defaultDates = {
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 86400000).toISOString(),
      };

      setSelectedDates(defaultDates);
      localStorage.setItem("modalBookingData", JSON.stringify(defaultDates));
    }
  }, [setSelectedDates]);

  const getCellDate = useCallback(
    (dayIndex, hour) => {
      const cellDate = new Date(weekDays[dayIndex]);
      cellDate.setHours(hour, 0, 0, 0);
      return cellDate;
    },
    [weekDays]
  );

  useEffect(() => {
    const savedDates = JSON.parse(localStorage.getItem("modalBookingData"));

    if (savedDates?.startDate && savedDates?.endDate) {
      setSelectedDates(savedDates);
    }
  }, [setSelectedDates]);

  useEffect(() => {
    if (hasTrailer && selectedDates?.startDate) {
      const start = new Date(selectedDates.startDate);
      const hh = String(start.getHours()).padStart(2, "0");
      const mm = String(start.getMinutes()).padStart(2, "0");

      setStartTime(`${hh}:${mm}`);
      setTrailerPickupTime(`${hh}:${mm}`);
    }
  }, [hasTrailer, selectedDates?.startDate, setTrailerPickupTime]);

  useEffect(() => {
    if (startDateTime || endDateTime || reservations.length === 0) return;

    const wasCleared =
      sessionStorage.getItem("trailerSelectionCleared") === "true";

    if (wasCleared) return;

    const now = new Date();

    for (let dayIdx = 0; dayIdx < weekDays.length; dayIdx++) {
      for (let hour = 6; hour <= 19; hour++) {
        const start = getCellDate(dayIdx, hour);
        const end = new Date(start.getTime() + 4 * 60 * 60 * 1000);

        if (start < now) continue;

        const slotIsFree = !reservations.some((res) =>
          isOverlap(new Date(res.start), new Date(res.end), start, end)
        );

        if (slotIsFree) {
          setStartDateTime(start);
          setEndDateTime(end);
          setStartPick({ dayIndex: dayIdx, slot: hour });

          setStartTime(formatSlotTime(start.getHours()));
          setEndTime(formatSlotTime(end.getHours()));
          return;
        }
      }
    }
  }, [reservations, weekDays, startDateTime, endDateTime, getCellDate]);

  // Päivitetään näytettävät kellonajat absoluuttisista varausajoista
  useEffect(() => {
    if (startDateTime) {
      setStartTime(formatSlotTime(startDateTime.getHours()));
    } else {
      setStartTime("");
    }

    if (endDateTime) {
      setEndTime(formatSlotTime(endDateTime.getHours()));
    } else {
      setEndTime("");
    }
  }, [startDateTime, endDateTime]);

  // Lasketaan vuokrauksen kesto absoluuttisista ajoista
  const selectedDuration = useMemo(() => {
    if (!startDateTime || !endDateTime) return null;

    const msDiff = endDateTime - startDateTime;

    if (msDiff <= 0) return null;

    const totalHours = msDiff / 3600000;
    const days = Math.floor(totalHours / 24);
    const hours = Math.round(totalHours % 24);

    return { days, hours };
  }, [startDateTime, endDateTime]);

  const selectedRangeHasReservation = useMemo(() => {
    if (!startDateTime || !endDateTime) return false;

    return reservations.some((res) =>
      isOverlap(
        new Date(res.start),
        new Date(res.end),
        startDateTime,
        endDateTime
      )
    );
  }, [reservations, startDateTime, endDateTime]);

  // Klikkaustoiminto: asetetaan absoluuttiset varausaikat ja suhteelliset solukuviot
  const handleSlotClick = (dayIndex, hour) => {
    const clickedDate = getCellDate(dayIndex, hour);

    // Jos klikataan samaa solua kuin aloitusaika, resetoi varaus
    if (startPick && startPick.dayIndex === dayIndex && startPick.slot === hour) {
      setStartPick(null);
      setStartDateTime(null);
      setEndDateTime(null);
      return;
    }

    // Jos aloitusaikaa ei ole asetettu, asetetaan se
    if (!startDateTime) {
      setStartDateTime(clickedDate);
      setStartPick({ dayIndex, slot: hour });
      setEndDateTime(null);
      return;
    }

    // Jos klikattu aika on ennen nykyistä aloitusaikaa, päivitetään aloitusaika
    if (clickedDate < startDateTime) {
      setStartDateTime(clickedDate);
      setStartPick({ dayIndex, slot: hour });
      setEndDateTime(null);
    } else {
      // Muussa tapauksessa asetetaan lopetusaika
      setEndDateTime(clickedDate);
    }
  };

  // Reset-nappi
  const clearSelection = () => {
    setStartDateTime(null);
    setEndDateTime(null);
    setStartPick(null);
    setStartTime("");
    setEndTime("");
    setSelectedDates(null);
    localStorage.removeItem("modalBookingData");

    sessionStorage.setItem("trailerSelectionCleared", "true");
  };

  // Varauksen tekeminen: käytetään absoluuttisia varausaikoja
  const handleReserve = () => {
    if (!startDateTime || !endDateTime) return;

    const durationHours = (endDateTime - startDateTime) / 3600000;

    if (durationHours < 4) {
      toast.error("Peräkärryä ei voi vuokrata alle 4 tunnin ajaksi.");
      return;
    }

    if (selectedRangeHasReservation) {
      toast.error("Valittu aikaväli osuu olemassa olevaan varaukseen.");
      return;
    }

    setSelectedDates({
      startDate: startDateTime.toISOString(),
      endDate: endDateTime.toISOString(),
    });

    localStorage.setItem(
      "modalBookingData",
      JSON.stringify({
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
      })
    );

    const hh = String(startDateTime.getHours()).padStart(2, "0");
    const mm = String(startDateTime.getMinutes()).padStart(2, "0");

    localStorage.setItem("trailerPickupTime", `${hh}:${mm}`);
    setTrailerPickupTime(`${hh}:${mm}`);

    const success = addTrailerToCart(startDateTime, endDateTime);

    if (success) {
      navigate("/tee-varaus");

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const addTrailerToCart = (start, end) => {
    const durationHours = (end - start) / 3600000;

    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const unitPrice = 25;
    const totalPrice = unitPrice * days;

    const trailerItem = {
      id: 24,
      name: "HIRVI 350",
      slug: "hirvi-350-kuomukarry",
      image: "/assets/perakarry.png",
      quantity: 1,

      rental: {
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        billableHours: durationHours,
      },

      pricing: {
        unitPrice,
        totalPrice,
        currency: "EUR",
      },

      flags: {
        isTrailer: true,
        requiresRentalTime: false,
        isPackage: false,
      },
    };

    const cart = JSON.parse(localStorage.getItem("rentalCart")) || [];

    const hasOtherThanTrailer = cart.some((item) => !item.flags?.isTrailer);

    if (hasOtherThanTrailer) {
      toast.error("Peräkärryn kanssa ei voi vuokrata muita tuotteita.");
      return false;
    }

    const newCart = cart.filter((c) => c.id !== 24);
    newCart.push(trailerItem);

    localStorage.setItem("rentalCart", JSON.stringify(newCart));
    window.dispatchEvent(new Event("cartUpdated"));

    return true;
  };

  const isSlotReserved = (dayIndex, hour) => {
    const d = getCellDate(dayIndex, hour);
    const dEnd = new Date(d);
    dEnd.setHours(dEnd.getHours() + 1);

    return reservations.some((res) => {
      const rStart = new Date(res.start);
      const rEnd = new Date(res.end);

      return isOverlap(rStart, rEnd, d, dEnd);
    });
  };

  const isSlotInRange = (dayIndex, hour) => {
    if (!startDateTime || !endDateTime) return false;

    const cellDate = getCellDate(dayIndex, hour);

    return cellDate >= startDateTime && cellDate < endDateTime;
  };

  const weekNumber = getISOWeekNumber(weekStart);

  const handlePrevWeek = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const prevMonday = addDays(weekStart, -7);

    if (prevMonday < today) {
      toast.info("Et voi tarkastella menneitä!");
      return;
    }

    setWeekStart(prevMonday);
  };

  const isRangeAvailable = () => {
    if (!startDateTime || !endDateTime) return false;

    const msDiff = endDateTime - startDateTime;

    return msDiff >= 4 * 3600000 && !selectedRangeHasReservation;
  };

  const handleNextWeek = () => {
    setWeekStart((old) => addDays(old, 7));
  };

  return (
    <section className={calendarCardClass} aria-labelledby="trailer-calendar-title">
      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute -right-20 -top-20 h-56 w-56
          rounded-full bg-primary/10 blur-3xl
          dark:bg-primary/10
        "
      />

      <div className="relative text-center">
        <span className={badgeClass}>Peräkärryn kalenteri</span>

        <h2 id="trailer-calendar-title" className={`${headingClass} mt-4`}>
          {`Vuokrauskalenteri – viikko ${weekNumber}`}
        </h2>

        <p className={`${mutedTextClass} mx-auto mt-4 max-w-3xl`}>
          Valitse aloitus- ja lopetusaika kalenterista. Punainen aika on jo
          varattu, turkoosi näyttää valitun alun ja violetti valitun lopun.
        </p>
      </div>

      {/* Viikkonavigaatio */}
      <div className="relative flex flex-col flex-wrap items-center justify-center gap-3 text-center md:flex-row">
        {weekStart > getCurrentWeekMonday() ? (
          <ResponsiveButton
            className="w-full font-customFont1 text-sm font-bold md:w-44"
            onClick={() => setWeekStart(getCurrentWeekMonday())}
          >
            Palaa tähän viikkoon
          </ResponsiveButton>
        ) : (
          <ResponsiveButton
            className="h-10 w-full font-customFont1 text-sm font-bold md:w-44"
            onClick={handlePrevWeek}
            icon={MdArrowBack}
            disabled={true}
          >
            Edellinen viikko
          </ResponsiveButton>
        )}

        <span
          className="
            rounded-full border border-[#DDE7E5] bg-[#F8FAFC] px-4 py-2
            font-customFont1 text-sm font-extrabold !text-[#111827]
            dark:border-darkborder dark:bg-darksurface dark:!text-white
          "
        >
          {weekDays[0].toLocaleDateString("fi-FI")} –{" "}
          {weekDays[6].toLocaleDateString("fi-FI")}
        </span>

        <ResponsiveButton
          className="h-10 w-full font-customFont1 text-sm font-bold md:w-44"
          onClick={handleNextWeek}
          icon={MdArrowForward}
        >
          Seuraava viikko
        </ResponsiveButton>
      </div>

      {/* Mobiilin paluunappi tuoteinfoon */}
      <div className="relative flex justify-center md:hidden">
        <div className="absolute -top-9 animate-bounce text-5xl !text-[#111827] dark:!text-white">
          <MdKeyboardArrowUp />
        </div>

        <ResponsiveButton
          onClick={() => {
            const el = document.getElementById("trailerinfo");

            if (el) {
              const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
              window.scrollTo({ top: y, behavior: "smooth" });
            }
          }}
          variant="artistcardbutton"
          className="flex w-56 items-center gap-2"
        >
          Katso kärryn tiedot
        </ResponsiveButton>
      </div>

      {/* Alkuaika / loppuaika */}
      <div className="grid grid-cols-1 gap-4 rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-5 dark:border-darkborder dark:bg-darksurface/80 sm:grid-cols-2">
        <div className="flex flex-col">
          <label
            htmlFor="startTime"
            className="mb-2 text-center font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] !text-[#111827] dark:!text-white"
          >
            Alkuaika
          </label>

          <input
            id="startTime"
            type="text"
            readOnly
            value={startTime}
            className={inputClass}
          />
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="endTime"
            className="mb-2 text-center font-customFont1 text-sm font-extrabold uppercase tracking-[0.1em] !text-[#111827] dark:!text-white"
          >
            Loppuaika
          </label>

          <input
            id="endTime"
            type="text"
            readOnly
            value={endTime}
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex w-full justify-center">
        <ResponsiveButton
          onClick={clearSelection}
          variant="cancel"
          className="h-10 w-72 font-customFont1 text-sm font-semibold"
        >
          Tyhjennä valinta
        </ResponsiveButton>
      </div>

      {/* Statuskortti */}
      <div
        className="
          rounded-3xl border border-primary/30 bg-primary/10 p-5 text-center
          dark:border-primary/30 dark:bg-primary/5
        "
      >
        {!startDateTime || !endDateTime ? (
          <p className="mx-auto max-w-3xl font-customFont1 text-base font-semibold leading-relaxed !text-[#111827] dark:!text-white sm:text-lg">
            Kalenteri ehdottaa ensimmäisellä kerralla automaattisesti
            ensimmäistä vapaata 4 tunnin ajanjaksoa.
            <br />
            Voit laajentaa varausta pidemmäksi valitsemalla myöhemmän loppuajan
            kalenterista.
            <br />
            <br />
            Voit tyhjentää valinnan kokonaan “Tyhjennä valinta” -napista.
            <br />
            <strong>Alle 4 tunnin varauksia ei voi tehdä.</strong>
            <br />
            Punainen väri tarkoittaa, että kärry on jo varattu kyseiselle ajalle.
          </p>
        ) : (
          <>
            {isRangeAvailable() ? (
              <div className="font-customFont1 text-base font-semibold leading-relaxed !text-[#111827] dark:!text-white sm:text-lg">
                <p className="inline-flex items-center justify-center gap-2 font-extrabold">
                  Aikaväli on vapaa
                  <span className="text-primary animate-pulse">
                    <MdCheckCircle size={22} />
                  </span>
                </p>

                <br />

                Alku:{" "}
                {startDateTime.toLocaleDateString("fi-FI", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}{" "}
                {formatSlotTime(startDateTime.getHours())}

                <br />

                Loppu:{" "}
                {endDateTime.toLocaleDateString("fi-FI", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}{" "}
                {formatSlotTime(endDateTime.getHours())}

                <br />

                {selectedDuration && (
                  <span className="mt-1 block text-sm font-semibold italic !text-[#4B5563] dark:!text-[#D1D5DB]">
                    Kesto:{" "}
                    {selectedDuration.days > 0
                      ? `${selectedDuration.days} pv `
                      : ""}
                    {selectedDuration.hours > 0
                      ? `${selectedDuration.hours} h`
                      : ""}
                  </span>
                )}
              </div>
            ) : (
              <p className="font-customFont1 text-lg font-extrabold text-red-600 dark:text-red-300">
                Aikaväli ei ole saatavilla ❌
              </p>
            )}
          </>
        )}

        <div className="mt-5 flex justify-center">
          <ResponsiveButton
            className="h-10 w-72 font-customFont1 text-sm font-semibold"
            onClick={handleReserve}
            disabled={!startDateTime || !endDateTime || !isRangeAvailable()}
            icon={MdCalendarToday}
          >
            Varaa tämä aika
          </ResponsiveButton>
        </div>
      </div>

      {/* Kalenteriruudukko */}
      <div className="overflow-auto rounded-3xl border border-[#DDE7E5] bg-[#F8FAFC]/80 p-3 dark:border-darkborder dark:bg-darksurface/80">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day, dayIdx) => (
              <div
                key={dayIdx}
                className="
                  rounded-xl bg-white px-2 py-3 text-center font-customFont1
                  text-xs font-extrabold uppercase tracking-[0.05em]
                  !text-[#111827]
                  dark:bg-[#111114] dark:!text-white
                "
              >
                {day.toLocaleDateString("fi-FI", {
                  weekday: "short",
                })}{" "}
                {day.getDate().toString().padStart(2, "0")}.
                {getMonthNumber(day)}
              </div>
            ))}
          </div>

          <div className="mt-1">
            {slotsArray.map((hour) => (
              <div key={hour} className="mb-1 grid grid-cols-7 gap-1">
                {weekDays.map((day, dayIndex) => {
                  const cellDate = new Date(day);
                  cellDate.setHours(hour, 0, 0, 0);

                  const now = new Date();
                  const isPast = cellDate < now;
                  const slotReserved = isSlotReserved(dayIndex, hour);
                  const slotSelected = isSlotInRange(dayIndex, hour);
                  const slotIsStart =
                    startDateTime &&
                    cellDate.getTime() === startDateTime.getTime();
                  const slotIsEnd =
                    endDateTime && cellDate.getTime() === endDateTime.getTime();

                  let bgClass =
                    "bg-white dark:bg-[#111114] hover:bg-primary/20 dark:hover:bg-primary/15";
                  let cursorClass = "cursor-pointer";
                  let extraClass = "!text-[#111827] dark:!text-white";

                  if (isPast) {
                    bgClass = "bg-gray-100 dark:bg-white/5 opacity-40";
                    cursorClass = "cursor-not-allowed";
                    extraClass = "!text-gray-400";
                  } else if (slotReserved) {
                    bgClass = "bg-red-500 dark:bg-red-600";
                    cursorClass = "cursor-not-allowed";
                    extraClass = "!text-white font-extrabold";
                  } else if (slotIsStart && slotIsEnd) {
                    bgClass = "bg-gradient-to-r from-primary to-secondary";
                    extraClass = "!text-[#111827] font-extrabold";
                  } else if (slotIsStart) {
                    bgClass = "bg-primary";
                    extraClass = "!text-[#111827] font-extrabold";
                  } else if (slotIsEnd) {
                    bgClass = "bg-secondary";
                    extraClass = "!text-white font-extrabold";
                  } else if (slotSelected) {
                    bgClass =
                      "bg-gradient-to-r from-primary/70 via-secondary/70 to-primary/70 animate-shimmer bg-[length:600%_100%]";
                    extraClass = "!text-[#111827] font-extrabold";
                  }

                  return (
                    <button
                      key={`${hour}-${dayIndex}`}
                      type="button"
                      onClick={() => {
                        if (!slotReserved && !isPast) {
                          handleSlotClick(dayIndex, hour);
                        }
                      }}
                      className={`
                        rounded-xl px-2 py-2 text-center font-customFont1
                        text-[10px] font-bold shadow-sm transition
                        hover:-translate-y-0.5
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                        sm:text-xs md:text-sm
                        ${bgClass} ${cursorClass} ${extraClass}
                      `}
                      title={
                        isPast
                          ? "Aika on mennyt"
                          : slotReserved
                            ? "Aikaväli on varattu"
                            : "Klikkaa valitaksesi tämä tunti"
                      }
                    >
                      {formatSlotTime(hour)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

WeeklyTrailerCalendar.propTypes = {
  reservations: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default WeeklyTrailerCalendar;